import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

interface SocialMediaLink {
  platform: string;
  url: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  role: 'user' | 'guide' | 'admin';
  bio?: string;
  phone?: string;
  socialMediaLinks?: SocialMediaLink[];
  emailVerified?: boolean;
  profileImageUrl?: string;
  iconUrl?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  bio?: string;
  phone?: string;
  socialMediaLinks?: SocialMediaLink[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing token on app load
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp && payload.exp < currentTime) {
          localStorage.removeItem('token');
          setLoading(false);
          return;
        }
      } catch (error) {
        localStorage.removeItem('token');
        setLoading(false);
        return;
      }

      // Validate token and get user data
      authAPI.getCurrentUser()
        .then((response) => {
          setUser(response.user);
        })
        .catch((error) => {
          console.error('Failed to get current user:', error);
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authAPI.login(email, password);
      
      // Store token
      localStorage.setItem('token', response.token);
      
      // Set user data
      setUser(response.user);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Login failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authAPI.register(userData);
      
      // Store token
      localStorage.setItem('token', response.token);
      
      // Set user data
      setUser(response.user);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 
        error.response?.data?.details || 
        'Registration failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setError(null);
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
